import styled, { css } from 'styled-components';

import BaseCustomIcon from '@components/utils/CustomIcon';
import { GetSCProps, PaletteColor, SizeUnit } from '@utils/types';

export const CustomIcon = styled(BaseCustomIcon)`
  display: flex;
`;

export const LabelContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Separator = styled.div<{ visible: boolean }>`
  height: ${(p) => (p.visible ? 1 : 0)}px;
  background-color: ${({ theme }) => theme.palette.lightBlueGrey};
`;

const positionMixin = ({ isOpen = false }) =>
  isOpen
    ? css`
        width: calc(100% + 2px);
        left: -1px;
      `
    : css`
        width: calc(100%);
        left: 0;
      `;
export const OptionsContainer = styled.div<{
  isOpen: boolean;
  hasError?: boolean;
  optionsZIndex?: number;
}>`
  position: absolute;
  top: 100%;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  z-index: ${({ optionsZIndex = 3 }) => optionsZIndex};
  background-color: ${({ theme }) => theme.palette.lynxWhite};
  ul,
  li {
    background-color: ${({ theme }) => theme.palette.lynxWhite};
  }
  ul {
    padding: 0;
    margin: 0;
    padding-top: 20px;
    max-height: 220px;
    overflow-y: scroll;
    list-style: none;
    position: relative;
    border: 1px solid
      ${({ theme, isOpen, hasError }) =>
        hasError
          ? theme.palette.tomato
          : isOpen
          ? theme.palette.inputBorderColorActive
          : theme.palette.inputBorderColor};
    border-top: none;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  li {
    padding: 10px 5px 10px 15px;
    height: 40px;
    color: ${(p) => p.theme.palette.matterHorn};
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  ${positionMixin}
`;

type ContainerProps = GetSCProps<
  'isOpen' | 'height',
  {
    displayPlaceholder: boolean;
    containerBackground?: PaletteColor;
    horizontalPaddings: SizeUnit;
    hasError?: boolean;
    disabled?: boolean;
  }
>;

const labelColorMixin = ({ displayPlaceholder, theme }: ContainerProps) =>
  displayPlaceholder
    ? css`
        color: ${theme?.palette.matterHorn};
        opacity: 0.8;
      `
    : css`
        color: ${theme?.palette.brownishGrey};
        opacity: 1;
        font-weight: 600;
      `;

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 0 ${(p) => p.horizontalPaddings};
  background-color: ${({ theme, containerBackground = 'lynxWhite' }) =>
    theme.palette[containerBackground]};
  position: relative;
  border: 1px solid
    ${({ theme, isOpen, hasError }) =>
      hasError
        ? theme.palette.tomato
        : isOpen
        ? theme.palette.inputBorderColorActive
        : theme.palette.inputBorderColor};
  border-radius: 8px;
  cursor: pointer;
  ${(p) =>
    p.isOpen
      ? css`
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          border-bottom: none;
        `
      : null}
  height: ${({ height }) => height ?? '68px'};
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.PHONE})`} {
    height: 58px;
  }
  box-sizing: border-box;
  ${LabelContainer} {
    ${labelColorMixin}
    display: flex;
    align-items: center;
  }
  position: relative;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.disabled ? 'none' : 'all')};
`;

export const Options = styled.ul`
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.palette.brownishGrey};
    border-radius: 2.5px;
    height: 59px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
    -webkit-border-radius: 3px;
    border-radius: 3px;
  }
`;

export const Title = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  font-size: 12px;
  opacity: 0.5;
  font-weight: 600;
  color: ${(p) => p.theme.palette.darkGray};
`;

type OptionProps = GetSCProps<
  'isActive',
  {
    isHighlighted: boolean;
  }
>;

const optionMixin = ({ isActive, isHighlighted, theme }: OptionProps) => {
  const backgroundColor = isHighlighted ? theme?.palette.gray87 : undefined;
  return isActive
    ? css`
        font-weight: 600;
        background-color: ${backgroundColor};
      `
    : (() => {
        if (isHighlighted) {
          return css`
            background-color: ${theme?.palette.gray87} !important;
          `;
        }
        return null;
      })();
};
export const Option = styled.li<OptionProps>`
  ${optionMixin}
`;
